import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import { connect } from 'react-redux';
import { denormalize } from 'normalizr';
import { Nav, NavItem, NavLink } from 'reactstrap';
import { SCHEMAS } from 'js/api/schemas';
import { ChartSelector, LoadingMessage, ReplaceLink } from 'js/components';
import { Glyph as RadarGlyph } from 'js/components/Charts/SeriesRadarChart';
import { useQuery } from 'js/hooks/useQuery';
import { useChartSearchParam } from 'js/hooks/useChartSearchParam';
import {
  load as loadOrg,
  loadOrgSnapshot,
  updateOrgSnapshot,
} from 'js/actions/org-actions';
import { selectAssessmentHistorySummaries } from 'js/store/reducers/queries';
import OrgSnapshotChrome from './OrgSnapshotChrome';
import OrgSnapshotCombinedRadar from './OrgSnapshotCombinedRadar';
import OrgSnapshotSettings from './OrgSnapshotSettings';
import {
  snapshotSetPath,
  snapshotSourceName,
  snapshotDisplayName,
  optionalSnapshotName,
  templateFromSnapshotPayload,
} from './org-snapshot-utils';
import {
  rubricTeamsToQuestionScores,
  rubricTeamsToTeamScores,
} from 'js/utils/rubric-org-scores';
import OrgTree from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgTree';
import RubricOrgTree from 'js/routes/Organizations/components/OrgComponent/Visualizations/RubricOrgTree';
import OrgRecommendations from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgRecommendations';
import SquadScorecard from 'js/routes/Organizations/components/OrgComponent/Visualizations/SquadScorecard';
import { TreeGlyph } from 'js/routes/Organizations/components/OrgComponent/Glyphs';

function OrgSnapshotReports({
  organization: liveOrganization,
  workspace,
  assessmentHistorySummaries,
  loadOrg,
  loadOrgSnapshot,
  updateOrgSnapshot,
}) {
  const { workspaceId, orgId, sourceType, sourceKey, snapshotId } = useParams();
  const location = useLocation();
  const [chartKey, setChartKey] = useChartSearchParam();
  const decodedKey = decodeURIComponent(sourceKey);
  const orgQueryId = `org-snapshot-org-${orgId}`;
  const snapshotQueryId = `org-snapshot-${orgId}-${snapshotId}`;

  const loadOrgQuery = useCallback(
    (qid) => loadOrg(orgId, qid),
    [loadOrg, orgId]
  );
  const { refresh: refreshOrg } = useQuery({
    queryId: orgQueryId,
    query: loadOrgQuery,
    schema: SCHEMAS.ORGANIZATION,
  });

  const loadSnapshotQuery = useCallback(
    (qid) => loadOrgSnapshot(orgId, snapshotId, qid),
    [loadOrgSnapshot, orgId, snapshotId]
  );
  const {
    refresh: refreshSnapshot,
    result: snapshot,
    isLoading,
  } = useQuery({
    queryId: snapshotQueryId,
    query: loadSnapshotQuery,
  });

  useEffect(() => {
    refreshOrg();
    refreshSnapshot();
  }, [refreshOrg, refreshSnapshot]);

  const payload = snapshot?.payload ?? {};
  const frozenOrg = payload.organization || liveOrganization;
  const snapshotAt = snapshot?.snapshot_at ?? null;
  const template = snapshot ? templateFromSnapshotPayload(snapshot) : null;
  const isRubric = snapshot?.source_type === 'rubric';

  const questionScores = useMemo(
    () =>
      payload.teams
        ? rubricTeamsToQuestionScores(
            payload.teams,
            payload.document,
            snapshotAt ? new Date(snapshotAt) : new Date()
          )
        : null,
    [snapshotAt, payload.document, payload.teams]
  );

  const teamScores = useMemo(
    () =>
      payload.teams
        ? rubricTeamsToTeamScores(
            payload.teams,
            payload.document,
            snapshotAt ? new Date(snapshotAt) : new Date()
          )
        : null,
    [snapshotAt, payload.document, payload.teams]
  );

  const orgCharts = useMemo(() => {
    if (!frozenOrg || !snapshot) {
      return [];
    }
    const radarChart = {
      key: 'radar',
      label: RadarGlyph,
      component: OrgSnapshotCombinedRadar,
      buttonProps: { 'data-chart-type': 'radar' },
      componentProps: {
        organization: frozenOrg,
        assessmentTemplate: template,
        questionScores,
        isLoading: false,
      },
    };

    if (isRubric) {
      return [
        radarChart,
        {
          key: 'tree',
          label: TreeGlyph,
          component: RubricOrgTree,
          buttonProps: { 'data-chart-type': 'tree' },
          componentProps: {
            organization: frozenOrg,
            rubricId: decodedKey,
            rubricTeams: payload.teams,
            rubricDocument: payload.document,
            workspaceId,
            teamScores,
            loading: false,
            error: null,
          },
        },
      ];
    }

    return [
      radarChart,
      {
        key: 'tree',
        label: TreeGlyph,
        component: OrgTree,
        buttonProps: { 'data-chart-type': 'tree' },
        componentProps: {
          organization: frozenOrg,
          assessmentKey: decodedKey,
          workspaceId,
          teamScores,
          loading: false,
          error: null,
        },
      },
    ];
  }, [
    decodedKey,
    frozenOrg,
    isRubric,
    payload,
    questionScores,
    snapshot,
    teamScores,
    template,
    workspaceId,
  ]);

  const setPath = snapshotSetPath(workspaceId, orgId, sourceType, decodedKey);
  const snapshotsCrumb = { label: 'Snapshots', to: setPath };
  const sourceName = snapshotSourceName({
    sourceKey: decodedKey,
    snapshots: snapshot ? [snapshot] : [],
    summaries: assessmentHistorySummaries,
  });
  const baseUrl = `${setPath}/${snapshotId}`;
  const isSettings = location.pathname.startsWith(`${baseUrl}/settings`);
  const reportTabs = (
    <Nav className="tab-nav nav-underline">
      <NavItem>
        <NavLink
          tag={ReplaceLink}
          data-view="recommendations"
          active={location.pathname.startsWith(`${baseUrl}/recommendations`)}
          to={{
            pathname: `${baseUrl}/recommendations`,
            search: location.search,
          }}
        >
          Overview
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          tag={ReplaceLink}
          data-view="squad-scorecard"
          active={location.pathname.startsWith(`${baseUrl}/squad-scorecard`)}
          to={{
            pathname: `${baseUrl}/squad-scorecard`,
            search: location.search,
          }}
        >
          Scorecard
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          tag={ReplaceLink}
          data-view="settings"
          active={isSettings}
          to={{
            pathname: `${baseUrl}/settings`,
            search: location.search,
          }}
        >
          Settings
        </NavLink>
      </NavItem>
    </Nav>
  );
  const chromeProps = {
    workspaceId,
    orgId,
    organization: frozenOrg || liveOrganization,
    workspace,
    sourceType,
    sourceKey: decodedKey,
    sourceName,
    extra: {
      fallbackTitle: '\u00a0',
      tabs: reportTabs,
      currentCrumb: snapshotsCrumb,
    },
  };

  if (isLoading && !snapshot) {
    return (
      <OrgSnapshotChrome {...chromeProps} organization={liveOrganization}>
        <LoadingMessage iconSize="2x">Loading snapshot…</LoadingMessage>
      </OrgSnapshotChrome>
    );
  }

  if (!snapshot) {
    return (
      <OrgSnapshotChrome {...chromeProps} organization={liveOrganization}>
        <p>This snapshot could not be found.</p>
      </OrgSnapshotChrome>
    );
  }

  return (
    <OrgSnapshotChrome
      workspaceId={workspaceId}
      orgId={orgId}
      organization={frozenOrg}
      workspace={workspace}
      sourceType={sourceType}
      sourceKey={decodedKey}
      sourceName={
        optionalSnapshotName(snapshot.source_name) ||
        optionalSnapshotName(template?.name) ||
        sourceName
      }
      snapshotName={
        snapshot.archived_state === 'archived'
          ? `${snapshotDisplayName(snapshot)} (archived)`
          : snapshotDisplayName(snapshot)
      }
      snapshotAt={snapshot.snapshot_at}
      extra={{
        currentCrumb: snapshotsCrumb,
        tabs: reportTabs,
        chart:
          !isSettings && orgCharts.length > 0 ? (
            <ChartSelector
              loading={false}
              error={null}
              charts={orgCharts}
              activeKey={chartKey}
              onChange={setChartKey}
            />
          ) : null,
      }}
    >
      <Switch>
        <Route
          path={`${baseUrl}/recommendations/:subOrgId?`}
          exact
          render={({ match }) => (
            <OrgRecommendations
              match={match}
              workspace={workspace}
              organization={frozenOrg}
              questionScores={questionScores}
              assessmentKey={decodedKey}
              templates={template ? [template] : []}
            />
          )}
        />
        <Route
          path={`${baseUrl}/squad-scorecard`}
          exact
          render={() => (
            <SquadScorecard
              workspace={workspace}
              organization={frozenOrg}
              questionScores={questionScores}
              assessmentKey={decodedKey}
              templates={template ? [template] : []}
            />
          )}
        />
        <Route
          path={`${baseUrl}/settings`}
          exact
          render={() => (
            <OrgSnapshotSettings
              snapshot={snapshot}
              orgId={orgId}
              updateOrgSnapshot={updateOrgSnapshot}
              onUpdated={refreshSnapshot}
            />
          )}
        />
        <Route
          path={baseUrl}
          exact
          render={() => (
            <Redirect
              to={{
                pathname: `${baseUrl}/recommendations`,
                search: location.search,
              }}
            />
          )}
        />
      </Switch>
    </OrgSnapshotChrome>
  );
}

export default connect(
  (store, { match }) => {
    const orgId = match?.params?.orgId;
    const workspaceId = match?.params?.workspaceId;
    return {
      organization: denormalize(
        store.entities.organizations[orgId],
        SCHEMAS.ORGANIZATION,
        store.entities
      ),
      workspace: store.entities.workspaces[workspaceId],
      assessmentHistorySummaries:
        selectAssessmentHistorySummaries(store, orgId)?.data ?? [],
    };
  },
  { loadOrg, loadOrgSnapshot, updateOrgSnapshot }
)(OrgSnapshotReports);
